import { parseOptionalFreeTilesLayout } from 'common/buyOneGetManyLayout';
import { Offer, OfferUI, RollingOfferProductSequence } from 'common/contracts';
import { ESaleDiscountDisplayType } from 'constants/enums';
import Decimal from 'decimal.js';
import { ROLLING_OFFER_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';
import { normalizeBgColor } from 'views/OfferDesign/OfferDesign.utils';
import {
  enrichProgressBarPoints,
  mapSequenceProduct
} from 'views/Offers/utils/offerProductSequence.utils';

export const resolveStrokedPriceDisplay = (
  item: RollingOfferProductSequence
): string | undefined => {
  const priceInDollars = item?.priceInUsdCents || 0;
  if (priceInDollars <= 0) return undefined;

  const discountValue = resolveDiscountValue(item);
  if (!discountValue || discountValue <= 0) return undefined;

  const discountType = item?.priceDiscount?.type || item?.productSale?.type;
  const originalPrice = resolveOriginalPrice(
    priceInDollars,
    discountValue,
    discountType
  );
  if (originalPrice === undefined) return undefined;
  return `$${originalPrice.toFixed(2)}`;
};

const resolveDiscountValue = (item: RollingOfferProductSequence): number => {
  if (
    item?.priceDiscount?.discount !== undefined &&
    item?.priceDiscount?.discount !== null
  ) {
    return item.priceDiscount.discount;
  }
  return item?.productSale?.sale || 0;
};

const resolveOriginalPrice = (
  priceInDollars: number,
  discountValue: number,
  discountType: ESaleDiscountDisplayType | undefined
): number | undefined => {
  if (discountType === ESaleDiscountDisplayType.PERCENTAGE) {
    if (discountValue >= 100) return undefined;
    return priceInDollars / (1 - discountValue / 100);
  }
  return priceInDollars + new Decimal(discountValue).div(100).toNumber();
};

export const mapProductsSequence = (
  productsSequence: RollingOfferProductSequence[],
  progressBarOffers: Offer[]
) => {
  return productsSequence.map((item) => {
    const strokedPriceDisplay = resolveStrokedPriceDisplay(item);
    return {
      ...item,
      prices: {
        priceInUsdCents: new Decimal(item?.priceInUsdCents || 0)
          .mul(100)
          .toNumber(),
        priceDisplay: `$${item?.priceInUsdCents || 0}`,
        ...(strokedPriceDisplay ? { strokedPriceDisplay } : {}),
        currencyCode: 'USD'
      },
      products: item?.products.map(mapSequenceProduct),
      salePercentage: item?.productSale?.sale || 0,
      salePercentageDisplayType: ESaleDiscountDisplayType.PERCENTAGE,
      badges: item?.badges || [],
      progressBarPoints: enrichProgressBarPoints(
        item?.progressBarPoints,
        progressBarOffers
      )
    };
  });
};

export type MappedBuyOneGetManyProduct = ReturnType<
  typeof mapProductsSequence
>[number];

export const truncateForBuyOneGetMany = (
  mappedProducts: MappedBuyOneGetManyProduct[],
  freeTilesCount?: number
): MappedBuyOneGetManyProduct[] => {
  if (!freeTilesCount || freeTilesCount <= 0) return mappedProducts;
  const desiredLength = 1 + freeTilesCount;
  if (mappedProducts.length <= desiredLength) return mappedProducts;
  return mappedProducts.slice(0, desiredLength);
};

export const reindexForBuyOneGetMany = (
  mappedProducts: MappedBuyOneGetManyProduct[]
): MappedBuyOneGetManyProduct[] => {
  const sorted = [...mappedProducts].sort((a, b) => a.index - b.index);
  return sorted.map((item, position) => ({ ...item, index: position }));
};

export interface ResolvedBuyOneGetManyDesignProps {
  headerImage: string | undefined;
  backgroundColor: ReturnType<typeof normalizeBgColor>;
  subBackgroundColor: ReturnType<typeof normalizeBgColor>;
  subBackgroundImage: string | undefined;
  freeTilesBackgroundImage: string | undefined;
  lockImage: string;
  collectText: string;
  plusImage: string | undefined;
  claimedIconImage: string | undefined;
  freeTilesColumns: number | undefined;
}

export const resolveBuyOneGetManyDesignProps = (
  offerDesignData?: OfferUI
): ResolvedBuyOneGetManyDesignProps => {
  const onePlusMany = offerDesignData?.onePlusMany;
  const subOffer = onePlusMany?.subOffer;
  return {
    headerImage: onePlusMany?.headerImage,
    backgroundColor:
      normalizeBgColor(offerDesignData?.backgroundColor) ||
      ROLLING_OFFER_DEFAULTS.backgroundColor,
    subBackgroundColor:
      normalizeBgColor(subOffer?.backgroundColor) ||
      ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor,
    subBackgroundImage: subOffer?.backgroundImage || undefined,
    freeTilesBackgroundImage: subOffer?.freeTilesBackgroundImage || undefined,
    lockImage: subOffer?.lockImage || ROLLING_OFFER_DEFAULTS.lockImage,
    collectText: onePlusMany?.collectText || ROLLING_OFFER_DEFAULTS.collectText,
    plusImage: onePlusMany?.plusImage,
    claimedIconImage: subOffer?.claimedImage || undefined,
    freeTilesColumns: parseOptionalFreeTilesLayout(onePlusMany?.freeTilesLayout)
  };
};
